import { SpotInterface, PricingPayload, SpotMetadata } from 'types';
import APIClient from './APIClient';
import { buildQueryParams } from 'utils/helpers';
import type { LocalIntelligenceEvent } from 'types/LocalIntelligenceEvent';
import type { LocalIntelligenceNearbyLot } from 'types/LocalIntelligenceNearbyLot';
import type { ReviewInterface } from 'types/Review';
import type { LocalIntelligenceNearbyLotPrice } from 'types/LocalIntelligenceNeabyLotPrice';
import { PagedResponse } from 'types/Pagination';

class SpotsService extends Object {
    getSpotsInfo(organization: number, token: string): Promise<SpotInterface[]> {
        // we should use /spotmetadata?organization=${organization}&includeThirdParty=true
        // path, but we can't because we need rates / prices. Check out AG-3400.
        return APIClient.get(`spots${buildQueryParams({ organization, includeThirdParty: true })}`, token);
    }

    getSpots(token: string): Promise<SpotMetadata[]> {
        return APIClient.get(`spotmetadata`, token);
    }

    getMultidayPrices(spotId: string, days: number, token: string): Promise<{ result: PricingPayload[] }> {
        return APIClient.get(`spots/${spotId}/multiday_pricing_history/${buildQueryParams({ days })}`, token);
    }

    getLocalEvents(
        spotUuid: string,
        params: { startDate?: string; endDate?: string; placekey?: string; limit?: number } = {},
        token: string
    ): Promise<LocalIntelligenceEvent[]> {
        return APIClient.get(`spots/${spotUuid}/local-events/${buildQueryParams(params)}`, token);
    }

    getNearbyLots(
        spotUuid: string,
        params: { maxDistance?: number; limit?: number },
        token: string
    ): Promise<LocalIntelligenceNearbyLot[]> {
        return APIClient.get(`spots/${spotUuid}/nearby-lots/${buildQueryParams(params)}`, token);
    }

    getNearbyLotPricing(
        spotUuid: string,
        nearbyLotId: string,
        token: string
    ): Promise<LocalIntelligenceNearbyLotPrice[]> {
        return APIClient.get(`spots/${spotUuid}/nearby-lots/${nearbyLotId}/`, token);
    }

    getReviews(
        spotPk: number,
        params: { limit?: number; page?: number; rating?: number },
        token: string
    ): Promise<PagedResponse<ReviewInterface>> {
        return APIClient.get(`spots/${spotPk}/reviews/${buildQueryParams(params)}`, token);
    }
}

export default new SpotsService();
